import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  adminLogin,
  getActiveRestaurants,
  getInactiveRestaurants,
  setRestaurantActive,
} from '../../services/api';
import ConfirmPasswordModal from './ConfirmPasswordModal';
import './AdminRestaurants.css';

const CONFIG = {
  active: { title: 'Active restaurants', load: getActiveRestaurants, empty: 'No active restaurants yet.' },
  inactive: { title: 'Inactive restaurants', load: getInactiveRestaurants, empty: 'No inactive restaurants. All caught up.' },
};

const SUBSCRIPTIONS = { 1: 'None', 2: 'Standard', 3: 'Base' };

const formatAddress = (r) => {
  const street = [r.street, r.street_number].filter(Boolean).join(' ');
  const city = [r.postal_code, r.city].filter(Boolean).join(' ');
  return [street, city, r.country].filter(Boolean).join(', ') || '—';
};

const AdminRestaurants = ({ status }) => {
  const { authenticatedFetch, email } = useAuth();
  const { title, load, empty } = CONFIG[status];
  const [restaurants, setRestaurants] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [target, setTarget] = useState(null);
  const [notice, setNotice] = useState('');

  const closeModal = useCallback(() => setTarget(null), []);

  const confirmToggle = async (password) => {
    // Re-verify the password with the same endpoint used to sign in.
    await adminLogin(email, password);
    const makeActive = !target.is_active;
    await setRestaurantActive(target.id, makeActive, authenticatedFetch);
    // The lists are split by status, so the restaurant leaves this one.
    setRestaurants((prev) => prev.filter((r) => r.id !== target.id));
    setNotice(`${target.name} was ${makeActive ? 'activated' : 'deactivated'}.`);
    setTarget(null);
  };

  const fetchData = useCallback(async () => {
    setRestaurants(null);
    setError('');
    try {
      setRestaurants(await load(authenticatedFetch));
    } catch (err) {
      setError(err.message);
    }
  }, [load, authenticatedFetch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!restaurants) return [];
    const sorted = [...restaurants].sort((a, b) => a.name.localeCompare(b.name));
    return q
      ? sorted.filter((r) => `${r.name} ${formatAddress(r)}`.toLowerCase().includes(q))
      : sorted;
  }, [restaurants, query]);

  return (
    <div className="admin-restaurants">
      <header className="admin-page-header">
        <div>
          <h1>{title}</h1>
          {restaurants && <p className="admin-page-count">{restaurants.length} total</p>}
        </div>
        <input
          type="search"
          className="admin-search"
          placeholder="Search by name or address"
          aria-label="Search restaurants"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </header>

      {notice && <div className="admin-notice" role="status">{notice}</div>}

      {error && (
        <div className="admin-state admin-state-error" role="alert">
          <p>{error}</p>
          <button type="button" className="admin-retry" onClick={fetchData}>Try again</button>
        </div>
      )}

      {!error && !restaurants && <div className="admin-state">Loading…</div>}

      {restaurants && !error && (
        filtered.length === 0 ? (
          <div className="admin-state">{query ? 'No restaurants match your search.' : empty}</div>
        ) : (
          <div className="admin-table-card">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Restaurant</th>
                  <th>Address</th>
                  <th>Subscription</th>
                  <th>Status</th>
                  <th className="admin-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id}>
                    <td className="admin-cell-name ph-no-capture">{r.name}</td>
                    <td className="ph-no-capture">{formatAddress(r)}</td>
                    <td>{SUBSCRIPTIONS[r.subscription] || '—'}</td>
                    <td>
                      <span className={`admin-badge ${r.is_active ? 'is-active' : 'is-inactive'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="admin-cell-actions">
                      <button
                        type="button"
                        className={`admin-action-btn ${r.is_active ? 'is-deactivate' : 'is-activate'}`}
                        onClick={() => { setNotice(''); setTarget(r); }}
                      >
                        {r.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {target && (
        <ConfirmPasswordModal
          title={`${target.is_active ? 'Deactivate' : 'Activate'} restaurant`}
          description={target.is_active
            ? `${target.name} will no longer be visible to customers.`
            : `${target.name} will become visible to customers.`}
          confirmLabel={target.is_active ? 'Deactivate' : 'Activate'}
          danger={target.is_active}
          onConfirm={confirmToggle}
          onClose={closeModal}
        />
      )}
    </div>
  );
};

export default AdminRestaurants;
